import type { Patient360AdverseEventDto } from '../types/patient360';

// ─── Lifecycle helpers ───────────────────────────────────────
// Single source of truth for AE/PC lifecycle normalization — shared between
// AdverseEventsTab (full detail view) and the Patient360 Needs Attention summary.

export type LifecyclePhase = 'DRAFT' | 'SUBMITTED' | 'PV_REVIEW' | 'CLOSED';

export function normalizePhase(status: string | null | undefined): LifecyclePhase {
  const s = (status || '').toUpperCase();
  if (s === 'SUBMITTED') return 'SUBMITTED';
  if (s === 'PV_REVIEW' || s === 'UNDER_REVIEW') return 'PV_REVIEW';
  if (s === 'CLOSED' || s === 'RESOLVED') return 'CLOSED';
  return 'DRAFT';
}

export function isSeriousEvent(severity: string | null | undefined): boolean {
  const s = (severity || '').toUpperCase();
  return s === 'SEVERE' || s === 'HIGH' || s === 'CRITICAL' || s === 'LIFE_THREATENING';
}

// ─── Smart alerts ─────────────────────────────────────────────

export interface AeSmartAlert {
  message: string;
  type: 'error' | 'warning' | 'info';
}

/**
 * Derives AE/PC safety alerts from a patient's adverse event list.
 * Pure function of existing data — no new business rules.
 */
export function computeAeSmartAlerts(events: Patient360AdverseEventDto[]): AeSmartAlert[] {
  const alerts: AeSmartAlert[] = [];

  const breached = events.filter(e => e.isSlaBreached && normalizePhase(e.status) !== 'CLOSED');
  if (breached.length > 0) {
    alerts.push({ message: `${breached.length} report(s) with breached SLA — PV review overdue`, type: 'error' });
  }

  const seriousNotEscalated = events.filter(e =>
    isSeriousEvent(e.severity) && normalizePhase(e.status) === 'DRAFT'
  );
  if (seriousNotEscalated.length > 0) {
    alerts.push({ message: `${seriousNotEscalated.length} serious event(s) not yet submitted — requires PV review within 24h`, type: 'warning' });
  }

  return alerts;
}
